/**
 * One way of talking to the service, and one way of describing it when that does not work.
 *
 * Every call goes through here so that a slow connection, a connection that has gone, a session
 * that has ended and a service that is broken are told apart once rather than in every screen. The
 * result is a {@link Failure}, which is what the shared feedback pattern renders: the areas never
 * see a raw `Response` or a `TypeError` from `fetch`.
 */

export type FailureKind =
  /** The device says it has no connection. */
  | 'offline'
  /** The service did not answer within the time we are prepared to wait. */
  | 'timeout'
  /** The request never arrived — nothing is listening, or the network dropped it. */
  | 'unreachable'
  /** The service answered, and the answer was no. */
  | 'refused'
  /** The service answered with something that went wrong on its side. */
  | 'service'
  /** The service answered with something we cannot read. */
  | 'malformed'

export interface Failure {
  readonly kind: FailureKind
  /** One sentence, fit to put in front of a person. No jargon, no status codes. */
  readonly message: string
  /** The technical remainder, shown quietly underneath when there is one. */
  readonly detail: string | null
  /** Whether trying the same thing again could plausibly work. */
  readonly retryable: boolean
  /**
   * The service's own name for this refusal, where it gave one — `edit-out-of-date`, `name-in-use`.
   *
   * Absent for everything that is not a refusal the service named. It is here for the few places that
   * have to *do* something different rather than say something different: the editor answers an
   * out-of-date save by going read-only and keeping what was typed, which is not a sentence but a
   * behaviour. Anything that only needs words uses {@link message}.
   */
  readonly code?: string | null
}

export class RequestFailed extends Error {
  readonly failure: Failure

  constructor(failure: Failure, options?: ErrorOptions) {
    super(failure.message, options)
    this.name = 'RequestFailed'
    this.failure = failure
  }
}

/**
 * How long a request may take before it is treated as not coming.
 *
 * Long enough for a phone on a bad connection, short enough that nobody is left looking at a
 * screen that never resolves. A request that runs out is a failure with a way to try again, never
 * a spinner that stays.
 */
export const REQUEST_TIMEOUT_MS = 15_000

export interface RequestOptions {
  readonly method?: string
  readonly headers?: Readonly<Record<string, string>>
  /** What the request carries. A caller that sends JSON says so in its own `Content-Type`. */
  readonly body?: BodyInit
  /** The caller's own abort — for a component that goes away while the request is in flight. */
  readonly signal?: AbortSignal
  readonly timeoutMs?: number
  /** Status codes handed back to the caller instead of being turned into a failure. */
  readonly expect?: readonly number[]
}

/**
 * The cookie the service puts its CSRF token in, and the header it wants it back in.
 *
 * The token proves a request came from this page and not from some other site that happens to have
 * the session cookie. It is written by the first `/api/session` call, so it is there before anything
 * can be changed — and reading it here means no screen has to remember to send it.
 */
const CSRF_COOKIE = 'XSRF-TOKEN'
const CSRF_HEADER = 'X-XSRF-TOKEN'

/** Methods that change something, and therefore need the token. */
const READ_ONLY = ['GET', 'HEAD', 'OPTIONS']

export function csrfHeaders(method: string): Record<string, string> {
  if (READ_ONLY.includes(method.toUpperCase())) {
    return {}
  }
  const token = readCookie(CSRF_COOKIE)
  return token === null ? {} : { [CSRF_HEADER]: token }
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null
  }
  const match = document.cookie.split('; ').find((pair) => pair.startsWith(`${name}=`))
  return match === undefined ? null : decodeURIComponent(match.slice(name.length + 1))
}

/**
 * Sends one request.
 *
 * Rejects with a {@link RequestFailed} for everything the caller cannot act on, and with the
 * caller's own `AbortError` when the caller aborted — a component that has gone away is not a
 * failure and must not be reported as one.
 */
export async function request(url: string, options: RequestOptions = {}): Promise<Response> {
  const {
    method = 'GET',
    headers,
    body,
    signal,
    timeoutMs = REQUEST_TIMEOUT_MS,
    expect = [],
  } = options

  const controller = new AbortController()
  let timedOut = false
  const timer = setTimeout(() => {
    timedOut = true
    controller.abort()
  }, timeoutMs)
  const relayAbort = () => {
    controller.abort()
  }
  signal?.addEventListener('abort', relayAbort, { once: true })

  let response: Response
  try {
    response = await fetch(url, {
      method,
      headers: { Accept: 'application/json', ...csrfHeaders(method), ...headers },
      body,
      signal: controller.signal,
    })
  } catch (cause) {
    if (signal?.aborted === true) {
      throw cause
    }
    if (timedOut) {
      throw new RequestFailed({
        kind: 'timeout',
        message: 'MyLib is taking longer than usual to answer.',
        detail: null,
        retryable: true,
      })
    }
    if (isOffline()) {
      throw new RequestFailed({
        kind: 'offline',
        message: 'You are offline. MyLib picks up again as soon as the connection is back.',
        detail: null,
        retryable: true,
      })
    }
    throw new RequestFailed(
      {
        kind: 'unreachable',
        message: 'MyLib is not answering.',
        detail: cause instanceof Error ? cause.message : String(cause),
        retryable: true,
      },
      { cause },
    )
  } finally {
    clearTimeout(timer)
    signal?.removeEventListener('abort', relayAbort)
  }

  if (response.ok || expect.includes(response.status)) {
    return response
  }

  if (response.status === 401 || response.status === 403) {
    throw new RequestFailed({
      kind: 'refused',
      message: 'Your session has ended.',
      detail: null,
      retryable: false,
    })
  }

  throw new RequestFailed({
    kind: 'service',
    message: 'MyLib could not do that just now.',
    detail: `The service answered with ${response.status} ${response.statusText}.`,
    retryable: response.status >= 500,
  })
}

/** Reads the body as JSON, and says so plainly when it is not. */
export async function readJson<T>(response: Response): Promise<T> {
  let payload: unknown
  try {
    payload = await response.json()
  } catch (cause) {
    throw new RequestFailed(
      {
        kind: 'malformed',
        message: 'MyLib answered with something unexpected.',
        detail: 'The answer was not JSON.',
        retryable: true,
      },
      { cause },
    )
  }

  if (typeof payload !== 'object' || payload === null) {
    throw malformed('The answer was not an object.')
  }

  return payload as T
}

export function malformed(detail: string): RequestFailed {
  return new RequestFailed({
    kind: 'malformed',
    message: 'MyLib answered with something unexpected.',
    detail,
    retryable: true,
  })
}

/** Turns anything that was thrown into something that can be put in front of a person. */
export function asFailure(error: unknown): Failure {
  if (error instanceof RequestFailed) {
    return error.failure
  }
  return {
    kind: 'unreachable',
    message: 'Something went wrong.',
    detail: error instanceof Error ? error.message : String(error),
    retryable: true,
  }
}

/** True only when the browser is sure. An unknown state is treated as connected. */
export function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false
}
